import { Clock, Keyboard, Megaphone, Palette, Plug, RefreshCw, Rocket, Search, ShoppingBag, ShoppingCart, Smartphone, Workflow, Boxes, Wrench } from "lucide-react";
import { Seo } from "../components/site/Seo";
import { Btn } from "../components/site/Btn";
import { Eyebrow, Words } from "../components/site/Typo";
import { HeroVideo } from "../components/site/HeroVideo";
import { HeroScene } from "../components/site/HeroScene";
import { BuildSequence } from "../components/site/BuildSequence";
import { FinalCta, spotMove } from "../components/site/Blocks";
import { Journey, StrategySection } from "../components/site/Strategy";
import { SalesAgents, StudioSection } from "../components/site/Growth";
import { OfferPanel } from "../components/site/Panel";
import { IntegrationsVisual, JarvisVisual, SitesVisual } from "../components/site/Visuals";
import { eur, useOfferConfig } from "../lib/offer";

const BENEFITS = [[ShoppingBag, "Une boutique pensée pour vendre", "magenta"], [Search, "Plus de visibilité sur Google", "cyan"],
  [Clock, "Du temps gagné au quotidien", "amber"], [Workflow, "Des automatisations utiles", "violet"]];

const Hero = () => (
  <header className="hero hero-v2" data-testid="home-hero">
    <HeroVideo />
    <div className="hero-grid">
      <div className="hero-copy">
        <Eyebrow><span className="live-dot" aria-hidden="true" />DIGITAL XPERIENCE · ÎLE MAURICE</Eyebrow>
        <h1 className="hero-title">
          <Words text="Sites, IA &" /><Words text="automatisation" className="grad-text" /><Words text="pour faire grandir votre business." />
        </h1>
        <p className="lead hero-lead">NovaSoft crée, modernise et connecte vos outils pour vous aider à vendre plus simplement, gagner du temps et travailler plus sereinement.</p>
        <div className="actions">
          <Btn href="#solutions" testid="hero-cta-solutions">Découvrir nos solutions</Btn>
          <Btn to="/contact/" variant="ghost" testid="hero-cta-contact">Parler de mon projet</Btn>
        </div>
      </div>
      <HeroScene />
    </div>
  </header>
);

const Benefits = () => (
  <section className="benefits" aria-label="Ce que NovaSoft vous apporte" data-testid="benefits-bar">
    {BENEFITS.map(([Icon, t, c], i) => (
      <div key={t} className="benefit" data-reveal style={{ "--d": `${i * 70}ms`, "--c": `var(--${c})` }}>
        <span><Icon size={20} aria-hidden="true" /></span><b>{t}</b>
      </div>
    ))}
  </section>
);

export default function Home() {
  const { prices } = useOfferConfig();
  return (
    <>
      <Seo path="/" />
      <Hero />
      <Benefits />
      <BuildSequence />
      <StrategySection />
      <div id="solutions" className="solutions">
        <OfferPanel id="web-title" testid="panel-web" eyebrow="CRÉATION DE SITES WEB" title="Sites & E‑commerce"
          sub="Des sites modernes et rapides, pensés pour transformer vos visiteurs en clients."
          bullets={[[Palette, "Design moderne"], [Smartphone, "Optimisé mobile"], [Search, "Référencement Google"], [ShoppingCart, "Parcours d’achat simplifié"]]}
          cta={{ to: "/sites-internet-maintenance/", label: "Voir l’offre", testid: "home-web-cta" }}
          note={<span data-testid="home-price-line">Création {eur(prices.creation)} · maintenance en option</span>}
          script="Des sites qui donnent envie d’acheter" visual={<SitesVisual />} />
        <OfferPanel id="ai-title" testid="panel-ai" tone="magenta" eyebrow="VOTRE ASSISTANT IA" title="Pack Jarvis IA"
          sub="Compatible ChatGPT, Claude, Gemini… : un ensemble d’assistants pour votre business."
          bullets={[[Rocket, "Plus productif"], [RefreshCw, "Moins de tâches répétitives"], [Megaphone, "Assistants spécialisés"], [Wrench, "Installation simple"]]}
          cta={{ to: "/ia-automatisation/#pack-jarvis", label: "Découvrir le pack", testid: "home-ai-cta" }}
          script="Plusieurs assistants. Un seul objectif : votre réussite." visual={<JarvisVisual />} />
        <SalesAgents />
        <OfferPanel id="api-title" testid="panel-api" tone="amber" eyebrow="CONNECTEZ VOTRE BUSINESS" title="API & Intégrations"
          sub="Synchronisez vos catalogues et connectez vos outils facilement."
          bullets={[[Boxes, "Catalogue synchronisé"], [RefreshCw, "Stocks à jour"], [Keyboard, "Moins de ressaisie"], [Plug, "Outils connectés"]]}
          cta={{ to: "/integrations-api/", label: "Voir les intégrations", testid: "home-api-cta" }}
          script="Des solutions pour vos passions" visual={<IntegrationsVisual />} />
      </div>

      <StudioSection />

      <section className="section" aria-labelledby="apercu-title">
        <div className="teaser spot" onPointerMove={spotMove} data-reveal data-testid="apercu-teaser">
          <div>
            <Eyebrow>APERÇU BOUTIQUE</Eyebrow>
            <h2 id="apercu-title">Voyez votre future boutique <span className="grad-text">avant de décider.</span></h2>
            <p className="lead">Indiquez l’adresse de votre site actuel : nous préparons un aperçu de votre boutique modernisée, vérifié par notre équipe avant envoi.</p>
            <small className="muted">Proposition graphique — maquette non contractuelle.</small>
          </div>
          <Btn to="/apercu-boutique/" testid="teaser-cta">Recevoir mon aperçu</Btn>
        </div>
      </section>

      <Journey />
      <FinalCta audit />
    </>
  );
}
